/**
 * MOCK DATA — replace with real API call when backend is ready.
 *
 * Privacy rules enforced at this layer:
 *   - coordinates are city-centre approximates only (±0.05° jitter added at render time)
 *   - no street address, zip, or precise location stored
 *   - name = first name + last initial only
 */

export const CATEGORIES = [
  { id: "all", label: "All", icon: "⊕" },
  { id: "veteran", label: "Veterans", icon: "🛡" },
  { id: "first_responder", label: "First Responders", icon: "🔥" },
  { id: "law_enforcement", label: "Law Enforcement", icon: "⭐" },
  { id: "gold_star", label: "Gold Star Families", icon: "★" },
  { id: "volunteer", label: "Volunteers", icon: "🤝" },
  { id: "donor", label: "Donors", icon: "♥" },
];

// Category colour tokens (match brand palette)
export const CAT_COLORS = {
  veteran: "#5f7158", // sage / green
  first_responder: "#a86642", // rust / orange
  law_enforcement: "#4a6b8a", // steel blue
  gold_star: "#8a7a42", // gold
  volunteer: "#7a5a8a", // purple
  donor: "#8a4a5a", // rose
};

// City centres — approximate only
const CITIES = {
  phoenix: [-112.07, 33.45],
  denver: [-104.99, 39.74],
  austin: [-97.74, 30.27],
  nashville: [-86.78, 36.17],
  chicago: [-87.63, 41.88],
  new_york: [-74.0, 40.71],
  los_angeles: [-118.24, 34.05],
  san_francisco: [-122.42, 37.77],
  seattle: [-122.33, 47.61],
  houston: [-95.37, 29.76],
  miami: [-80.19, 25.77],
  atlanta: [-84.39, 33.75],
  washington_dc: [-77.04, 38.91],
  boston: [-71.06, 42.36],
  dallas: [-96.8, 32.78],
  minneapolis: [-93.27, 44.98],
  toronto: [-79.38, 43.65],
  vancouver: [-123.12, 49.28],
  calgary: [-114.07, 51.05],
  edmonton: [-113.49, 53.54],
  winnipeg: [-97.14, 49.9],
  anchorage: [-149.9, 61.22],
  mexico_city: [-99.13, 19.43],
};

// Small random offset so stacked city pins don't overlap (still city-level only)
function jitter(coord, amount = 0.45) {
  return coord + (Math.random() - 0.5) * amount;
}

function make(id, category, firstName, lastInitial, city, cityKey, retreat, rating, quote, portrait = null) {
  const [lng, lat] = CITIES[cityKey];
  return {
    id,
    type: "Feature",
    geometry: { type: "Point", coordinates: [jitter(lng), jitter(lat)] },
    properties: {
      id,
      category,
      name: `${firstName} ${lastInitial}.`,
      city,
      retreat,
      rating,
      quote,
      portrait, // path to portrait image — null = use placeholder
      storyUrl: "#stories",
    },
  };
}

export const PARTICIPANTS = [
  // Veterans
  make(1, "veteran", "John", "D", "Phoenix, Arizona", "phoenix", "Retreat 212", 5, "I finally felt understood. Forge gave me my life back."),
  make(2, "veteran", "Marcus", "T", "Denver, Colorado", "denver", "Retreat 212", 5, "Seven days changed everything. I came home to myself."),
  make(3, "veteran", "Carlos", "R", "Austin, Texas", "austin", "Retreat 213", 5, "For the first time in years, I felt like a person again."),
  make(4, "veteran", "Derek", "H", "Nashville, Tennessee", "nashville", "Retreat 212", 4, "The brotherhood I found here is unlike anything else."),
  make(5, "veteran", "Anthony", "M", "Chicago, Illinois", "chicago", "Retreat 213", 5, "Forge didn't just help me—it saved my family too."),
  make(6, "veteran", "James", "W", "Seattle, Washington", "seattle", "Retreat 214", 5, "I stopped carrying it alone. That changed everything."),
  make(7, "veteran", "Robert", "K", "Los Angeles, California", "los_angeles", "Retreat 213", 4, "Real work. Real people. Real results."),
  make(8, "veteran", "Miguel", "S", "San Antonio, Texas", "houston", "Retreat 212", 5, "Finally a program that treats the whole person."),
  make(9, "veteran", "Thomas", "B", "Boston, Massachusetts", "boston", "Retreat 214", 5, "I walked away with tools that actually work."),
  make(10, "veteran", "Kevin", "L", "Atlanta, Georgia", "atlanta", "Retreat 213", 5, "The system failed me. Forge didn't."),

  // First Responders
  make(11, "first_responder", "Ryan", "O", "Dallas, Texas", "dallas", "Retreat 212", 5, "I was carrying 18 years of calls. Forge helped me set them down."),
  make(12, "first_responder", "Brian", "C", "Chicago, Illinois", "chicago", "Retreat 213", 5, "Paramedic for 22 years. This is what we actually need."),
  make(13, "first_responder", "Jason", "P", "Houston, Texas", "houston", "Retreat 212", 4, "Fire never leaves you. But you can learn to breathe again."),
  make(14, "first_responder", "Scott", "N", "Minneapolis, Minnesota", "minneapolis", "Retreat 213", 5, "The weight I was carrying—I didn't even know how heavy it was."),
  make(15, "first_responder", "Chris", "V", "Miami, Florida", "miami", "Retreat 214", 5, "I can be present with my kids again. That's everything."),
  make(16, "first_responder", "Daniel", "A", "Los Angeles, California", "los_angeles", "Retreat 213", 4, "Seven days in Colombia. A lifetime of healing started."),
  make(17, "first_responder", "Matt", "Z", "Nashville, Tennessee", "nashville", "Retreat 212", 5, "The brotherhood in that circle changed how I see myself."),
  make(18, "first_responder", "Eric", "F", "Denver, Colorado", "denver", "Retreat 214", 5, "I called my wife from Colombia and said 'I'm back.'"),

  // Law Enforcement
  make(19, "law_enforcement", "David", "H", "Washington, DC", "washington_dc", "Retreat 212", 5, "Twenty years on the force. This is what should exist for all of us."),
  make(20, "law_enforcement", "Steven", "G", "New York, New York", "new_york", "Retreat 213", 4, "You can't pour from an empty cup. Forge filled mine."),
  make(21, "law_enforcement", "Patrick", "J", "Boston, Massachusetts", "boston", "Retreat 212", 5, "I came home different. Forge helped me get back to who I was."),
  make(22, "law_enforcement", "Aaron", "Q", "Atlanta, Georgia", "atlanta", "Retreat 213", 5, "The hypervigilance, the distance—I didn't have to carry it alone."),
  make(23, "law_enforcement", "Michael", "Y", "Chicago, Illinois", "chicago", "Retreat 214", 5, "I stopped taking the job home the day I got back."),
  make(24, "law_enforcement", "Greg", "X", "Austin, Texas", "austin", "Retreat 212", 4, "This isn't group therapy. It's something better."),
  make(25, "law_enforcement", "Paul", "I", "Seattle, Washington", "seattle", "Retreat 213", 5, "My sergeant asked what changed. I said Colombia."),
  make(26, "law_enforcement", "Nathan", "U", "Dallas, Texas", "dallas", "Retreat 214", 5, "Real accountability in that circle. That's what we needed."),

  // Gold Star Families
  make(27, "gold_star", "Sarah", "M", "Phoenix, Arizona", "phoenix", "Retreat 212", 5, "My son gave everything. Forge helped me find a way to honour that."),
  make(28, "gold_star", "Linda", "T", "Denver, Colorado", "denver", "Retreat 213", 5, "Grief doesn't go away. But it gets lighter when you're not alone."),
  make(29, "gold_star", "Patricia", "R", "Nashville, Tennessee", "nashville", "Retreat 212", 4, "I finally allowed myself to heal without guilt."),
  make(30, "gold_star", "Karen", "B", "San Francisco, CA", "san_francisco", "Retreat 213", 5, "They understood my loss in a way no one else had."),
  make(31, "gold_star", "Nancy", "W", "Washington, DC", "washington_dc", "Retreat 214", 5, "Seven days gave me a framework to carry forward, not just survive."),
  make(32, "gold_star", "Susan", "C", "Boston, Massachusetts", "boston", "Retreat 212", 5, "My daughter would have wanted this for me."),
  make(33, "gold_star", "Barbara", "L", "Atlanta, Georgia", "atlanta", "Retreat 213", 4, "The community Forge built around us is unlike anything else."),

  // Volunteers
  make(34, "volunteer", "Emily", "H", "Toronto, Ontario", "toronto", "Retreat 212", 5, "Supporting these warriors is the most meaningful work I've ever done."),
  make(35, "volunteer", "Jessica", "A", "Vancouver, BC", "vancouver", "Retreat 213", 5, "Every hour I give here comes back tenfold in purpose."),
  make(36, "volunteer", "Ashley", "K", "Calgary, Alberta", "calgary", "Retreat 212", 4, "The retreat team is the most dedicated group I've worked with."),
  make(37, "volunteer", "Amanda", "S", "Edmonton, Alberta", "edmonton", "Retreat 213", 5, "You don't leave the same person. Whether you're giving or receiving."),
  make(38, "volunteer", "Megan", "P", "Winnipeg, Manitoba", "winnipeg", "Retreat 214", 5, "Serving alongside veterans gave me perspective I'll carry forever."),
  make(39, "volunteer", "Rachel", "N", "Minneapolis, Minnesota", "minneapolis", "Retreat 212", 4, "The care that goes into every detail of this program is extraordinary."),
  make(40, "volunteer", "Stephanie", "O", "Chicago, Illinois", "chicago", "Retreat 213", 5, "I came to give. I left having received more than I gave."),

  // Donors
  make(41, "donor", "William", "F", "New York, New York", "new_york", "Supporter", 5, "I've funded a lot of programs. None move me the way Forge does."),
  make(42, "donor", "Charles", "E", "Boston, Massachusetts", "boston", "Supporter", 5, "The ROI here isn't financial. It's lives rebuilt."),
  make(43, "donor", "Richard", "D", "Washington, DC", "washington_dc", "Supporter", 4, "My brother was a veteran. I give so others get what he deserved."),
  make(44, "donor", "Joseph", "C", "San Francisco, CA", "san_francisco", "Supporter", 5, "Transparent. Accountable. Effective. Three words that are rare together."),
  make(45, "donor", "Thomas", "B", "Los Angeles, California", "los_angeles", "Supporter", 5, "I visited Colombia. Seeing the work in person—there's nothing like it."),
  make(46, "donor", "George", "A", "Seattle, Washington", "seattle", "Supporter", 4, "Forge does more per dollar than any program I've seen."),
  make(47, "donor", "Kenneth", "Z", "Dallas, Texas", "dallas", "Supporter", 5, "I donate because the system failed these people. Forge doesn't."),
  make(48, "donor", "Edward", "Y", "Miami, Florida", "miami", "Supporter", 5, "Every retreat funded is a family reunited. That's my 'why.'"),

  // Additional spread
  make(49, "veteran", "Luis", "V", "Houston, Texas", "houston", "Retreat 214", 5, "Colombia changed my trajectory. Full stop."),
  make(50, "veteran", "Harold", "U", "Anchorage, Alaska", "anchorage", "Retreat 212", 5, "Remote, forgotten, then found—that's my story with Forge."),
  make(51, "first_responder", "Frank", "T", "Toronto, Ontario", "toronto", "Retreat 213", 4, "Cross-border brothers and sisters in that retreat circle."),
  make(52, "veteran", "Raymond", "S", "Vancouver, BC", "vancouver", "Retreat 214", 5, "The mountains of Colombia mirrored where I was inside. Then cleared."),
  make(53, "law_enforcement", "Jack", "R", "Calgary, Alberta", "calgary", "Retreat 212", 5, "First time I told the truth about what the job costs. That room held it."),
  make(54, "gold_star", "Maria", "Q", "Mexico City, Mexico", "mexico_city", "Retreat 213", 5, "Grief has no borders. Neither does this community."),
  make(55, "first_responder", "Steve", "P", "Edmonton, Alberta", "edmonton", "Retreat 214", 5, "I stopped counting the bad calls and started counting the good days."),
  make(56, "veteran", "Gary", "O", "Winnipeg, Manitoba", "winnipeg", "Retreat 212", 4, "Seven days. The rest of my life. Worth every minute."),
  make(57, "donor", "Larry", "N", "Minneapolis, Minnesota", "minneapolis", "Supporter", 5, "My company sponsors a retreat slot each year. Best investment we make."),
  make(58, "volunteer", "Sandra", "M", "Atlanta, Georgia", "atlanta", "Retreat 213", 5, "Logistics lead for Retreat 213. The transformation I witnessed was real."),
  make(59, "law_enforcement", "Donald", "L", "Miami, Florida", "miami", "Retreat 214", 4, "PTSD doesn't discriminate by badge colour. Neither does Forge."),
  make(60, "gold_star", "Betty", "K", "Mexico City, Mexico", "mexico_city", "Retreat 212", 5, "They told our stories with honour. That's all we needed."),
];

export default PARTICIPANTS;
